import React from 'react';
import { 
    ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, 
    PieChart, Pie, Cell 
} from 'recharts';
import { createSafeMarkdown } from '../../utils/safeMarkdown';
import { ICONS, RISK_LEVEL_COLORS, SUITE_DETAILS } from '../constants';
import { normalizeRiskScore } from '../../utils/mapUtils';
import { RecommendedSuite, Page, NavParams } from '@types';
import { formatAcapsPopulation } from '../../utils/acapsPopulation';
import { formatLocalDate, formatLocalDateTime } from '../../utils/localDateTime';

export { ICONS, RISK_LEVEL_COLORS, SUITE_DETAILS, normalizeRiskScore };
export type { RecommendedSuite, Page, NavParams };

export const md = createSafeMarkdown({
    html: true,
    linkify: true,
    typographer: true
});

export const Section: React.FC<{ title: string; children: React.ReactNode; className?: string; id?: string }> = ({ title, children, className = '', id }) => (
    <div id={id} className={`report-print-section mt-6 ${className}`}>
        <h4 className="text-lg font-bold text-slate-800 border-b pb-1 mb-4">{title}</h4>
        {children}
    </div>
);

export const DefList: React.FC<{ items: { term: string; definition: React.ReactNode }[]; twoColumn?: boolean }> = ({ items, twoColumn = false }) => (
    <dl className={twoColumn ? "grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4" : "space-y-4"}>
        {items.map(item => (
            <div key={item.term} className="break-inside-avoid">
                <dt className="text-sm font-semibold text-slate-600">{item.term}</dt>
                <dd className="text-sm text-slate-800 mt-1 prose prose-sm max-w-none">{item.definition}</dd>
            </div>
        ))}
    </dl>
);

export const formatTimestamp = (value: unknown): string | null => formatLocalDateTime(value) || null;

// Helper to recursively remove undefined values AND unwrap Firestore REST objects ({stringValue, integerValue, etc.})
export const sanitizeFirestoreData = (data: any): any => {
    if (data === null || typeof data === 'undefined' || typeof data !== 'object') {
        return data;
    }

    // 1. Recursive Unwrap for Firestore REST Types
    if ('mapValue' in data) return sanitizeFirestoreData(data.mapValue.fields || {});
    if ('stringValue' in data) return data.stringValue;
    if ('doubleValue' in data) return Number(data.doubleValue);
    if ('integerValue' in data) return Number(data.integerValue);
    if ('booleanValue' in data) return data.booleanValue;
    if ('timestampValue' in data) return data.timestampValue;
    if ('arrayValue' in data) {
        return (data.arrayValue.values || []).map((v: any) => sanitizeFirestoreData(v));
    }

    // 2. Handle standard objects and arrays recursively
    if (Array.isArray(data)) {
        return data.map(sanitizeFirestoreData);
    }

    const cleanedObj: any = {};
    for (const [key, value] of Object.entries(data)) {
        if (typeof value !== 'undefined') {
            cleanedObj[key] = sanitizeFirestoreData(value);
        }
    }
    return cleanedObj;
};

export const Disclaimer: React.FC<{ text?: string; className?: string }> = ({ text, className = '' }) => {
    const defaultText = "This report was generated by Insights using Open-Source Intelligence (OSINT). The information is derived from publicly available sources at the time of generation and has not been independently verified by human analysts. This document is for informational and strategic planning purposes only and should not be considered a definitive intelligence product or the sole basis for decision-making. All information should be independently verified.";
    
    const hasAcademicGrounding = text?.includes("academic literature") || text?.includes("think-tank research");

    return (
        <div className={`mt-8 pt-6 border-t-2 ${hasAcademicGrounding ? 'border-indigo-200 bg-indigo-50/30' : 'border-slate-300'} rounded-b-xl px-4 pb-4 ${className}`}>
            <div className="flex items-start gap-3">
                <div className={`mt-0.5 ${hasAcademicGrounding ? 'text-indigo-600' : 'text-slate-400'}`}>
                    {hasAcademicGrounding ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                        </svg>
                    ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                    )}
                </div>
                <div className="flex-grow">
                    <p className={`text-xs ${hasAcademicGrounding ? 'text-indigo-800 font-bold' : 'text-slate-500 font-semibold'} uppercase tracking-wider mb-1`}>
                        {hasAcademicGrounding ? 'Academic Intelligence Grounding' : 'System Intelligence Disclaimer'}
                    </p>
                    <p className="text-[11px] leading-relaxed text-slate-500">
                        {text || defaultText}
                    </p>
                </div>
            </div>
        </div>
    );
};

export const Indicator: React.FC<{ label: string; value: string | number; unit?: string; date?: string; helpText?: string }> = ({ label, value, unit, date, helpText }) => (
    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 flex-1 transition-all hover:bg-slate-100 shadow-sm" title={helpText}>
        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-tight mb-1">{label}</div>
        <div className="text-2xl font-black text-slate-900 leading-none">
            {value}
            {unit && <span className="text-sm ml-1 text-slate-400 font-bold">{unit}</span>}
        </div>
        {date && <div className="text-[10px] text-slate-400 mt-1.5 font-bold tracking-tighter">REF: {formatLocalDate(date, date)}</div>}
    </div>
);

export const RiskFactorBars: React.FC<{ data: any[] }> = ({ data }) => {
    if (!data || data.length === 0) return null;
    
    return (
        <div className="space-y-4">
            {data.map((riskItem, i) => {
                const label = typeof riskItem === 'string' ? riskItem : riskItem.factor || riskItem.name || 'Critical Risk';
                const score = normalizeRiskScore(typeof riskItem === 'object' ? (riskItem.score || 50) : 50);
                const colorClass = score >= 80 ? 'bg-red-500' : score >= 50 ? 'bg-orange-500' : 'bg-green-500';
                
                return (
                    <div key={i} className="group">
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-black text-slate-700 uppercase tracking-tight">{label}</span>
                            <span className="text-[10px] font-black text-slate-400">{score}/100</span>
                        </div>
                        <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden shadow-inner">
                            <div 
                                className={`h-full ${colorClass} transition-all duration-1000 ease-out rounded-full shadow-sm`} 
                                style={{ width: `${score}%` }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};


export const InfrastructureInventory: React.FC<{ data: any[] }> = ({ data }) => {
    if (!data || data.length === 0) return null;
    
    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
            <div className="bg-slate-900 px-4 py-2 flex justify-between items-center">
                <h4 className="text-[10px] font-black text-white uppercase tracking-widest">Asset Inventory & Readiness</h4>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{data.length} Nodes Identified</span>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-xs">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="px-4 py-2 text-left font-black text-slate-500 uppercase tracking-tighter">Asset Name</th>
                            <th className="px-4 py-2 text-left font-black text-slate-500 uppercase tracking-tighter">Category</th>
                            <th className="px-4 py-2 text-left font-black text-slate-500 uppercase tracking-tighter">Status/Description</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                        {data.map((item, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                <td className="px-4 py-2 font-bold text-slate-900 border-l-2 border-l-transparent hover:border-l-blue-500">{item.name}</td>
                                <td className="px-4 py-2">
                                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter ${
                                        item.type === 'Military' ? 'bg-red-100 text-red-700' :
                                        item.type === 'Energy' ? 'bg-amber-100 text-amber-700' :
                                        item.type === 'Transport' ? 'bg-blue-100 text-blue-700' :
                                        'bg-slate-100 text-slate-600'
                                    }`}>
                                        {item.type}
                                    </span>
                                </td>
                                <td className="px-4 py-2 text-slate-500 italic">{item.description || 'Operational'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

interface ChartItem { 
    title?: string; 
    type: 'bar' | 'pie'; 
    data: { name: string; value: number }[]; 
}

export const ChartDisplay: React.FC<{ chartData: ChartItem }> = ({ chartData }) => {
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

    if (chartData.type === 'bar') {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData.data} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fill: '#475569', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #cbd5e1' }} />
                    <Bar dataKey="value" fill="#3b82f6" />
                </BarChart>
            </ResponsiveContainer>
        );
    }

    if (chartData.type === 'pie') {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={chartData.data}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                    >
                        {chartData.data.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #cbd5e1' }} />
                </PieChart>
            </ResponsiveContainer>
        );
    }
    return null;
};

export const QuantitativeIndicatorsDisplay: React.FC<{ indicators: any }> = ({ indicators }) => {
    if (!indicators) return null;
    
    const { acaps, worldBank } = indicators;
    
    return (
        <Section title="Quantitative Intelligence Metrics">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {acaps && (
                    <>
                        <Indicator 
                            label="Active Crises" 
                            value={acaps.crisisCount} 
                            helpText="Number of active humanitarian crises tracked by ACAPS."
                        />
                        <Indicator 
                            label="Total Affected" 
                            value={formatAcapsPopulation(acaps.totalAffected)}
                            helpText="Total number of people affected by active crises."
                        />
                    </>
                )}
                {worldBank && (
                    <>
                        <Indicator 
                            label="GDP Growth" 
                            value={worldBank.gdpGrowth || 'N/A'} 
                            unit="%" 
                            date={worldBank.dataYear}
                        />
                        <Indicator 
                            label="Annual Inflation" 
                            value={worldBank.inflation || 'N/A'} 
                            unit="%" 
                            date={worldBank.dataYear}
                        />
                    </>
                )}
            </div>
        </Section>
    );
};


